import { CSSProperties, useCallback, useMemo, useState } from 'react'
import { Path, Transforms } from 'slate'
import { ReactEditor, RenderElementProps, useSlateStatic } from 'slate-react'

import { getClearMicrosoftFileUrl } from 'core-components/helpers/microsoftFilesHelper'
import { ModalDialog } from 'core-components/library/modalDialog'
import {
  DEFAULT_VALUE_FOR_IFRAME,
  FILE_AVAILABLE_MIME_TYPES,
} from 'core-components/library/richTextEditor/constants'
import {
  downloadFile,
  openInNewTab,
} from 'core-components/library/richTextEditor/helpers/fileHelper'
import { updateIFrame } from 'core-components/library/richTextEditor/helpers/slateHelper'
import { TIFrameElement } from 'core-components/library/richTextEditor/interface/custom-types'
import { TIFrame } from 'core-components/library/richTextEditor/interface/TIFrame'
import { IFrameSettingsDialog } from 'core-components/library/richTextEditor/toolbar/components/IFrame/IFrameSettingsDialog'

import { TopPanelButtonsWrapper } from '../topPanelButtonsWrapper/TopPanelButtonsWrapper'

import styles from './IFrame.module.scss'

interface IProps {
  style: CSSProperties
}

export const IFrame = (props: RenderElementProps & IProps) => {
  const [iframeSettingsDialogVisible, setIFrameSettingsDialogVisible] = useState<boolean>(false)

  const element = useMemo(() => props.element as TIFrameElement, [props.element])

  const isFileIframe = useMemo(() => {
    return FILE_AVAILABLE_MIME_TYPES.some((el) => element.url?.includes(`.${el.name}`))
  }, [element.url])

  const editor = useSlateStatic()

  const path = ReactEditor.findPath(editor, element)

  const onDelete = useCallback(() => {
    const parentPath = Path.parent(path)
    Transforms.removeNodes(editor, {
      at: parentPath,
    })
    if (editor.onFileRemove && element.url) {
      const preparedUrl = getClearMicrosoftFileUrl(element.url)
      editor.onFileRemove(preparedUrl)
    }
  }, [path, editor, element.url])

  const addIFrame = useCallback(
    (settings: TIFrame) => {
      updateIFrame(editor, path, settings)
      setIFrameSettingsDialogVisible(false)
    },
    [editor, path]
  )

  const onSave = useCallback(() => {
    if (element && element.url) {
      const url = element.url
      const clearUrl = getClearMicrosoftFileUrl(url)
      downloadFile(clearUrl, element.name)
      editor.onFileDownload && editor.onFileDownload(url)
    }
  }, [element, editor])

  const onOpenView = useCallback(() => {
    if (element && element.url) {
      const url = element.url
      if (url && url.endsWith('.pdf')) {
        return openInNewTab(url)
      }
    }
    return
  }, [element])

  const onOpenSettings = useCallback(() => {
    setIFrameSettingsDialogVisible(true)
  }, [])

  const TopPanelButtonsWrapperOptions = useMemo(() => {
    if (isFileIframe) {
      const props = {
        onOpenView,
      }
      if (!editor.disabled) {
        Object.assign(props, { onDelete, onSave, onOpenSettings })
      }
      return props
    }
    return {
      onDelete,
      onOpenSettings,
    }
  }, [editor.disabled, isFileIframe, onDelete, onOpenSettings, onOpenView, onSave])

  return (
    <div>
      <ModalDialog
        title={'IFrame Settings'}
        content={IFrameSettingsDialog}
        contentProps={{
          iframeSettings: element,
          onSave: (settings: TIFrame) => {
            addIFrame(settings)
          },
          onCancel: () => setIFrameSettingsDialogVisible(false),
        }}
        size={'auto'}
        onClose={() => setIFrameSettingsDialogVisible(false)}
        show={iframeSettingsDialogVisible}
      />
      <TopPanelButtonsWrapper {...TopPanelButtonsWrapperOptions}>
        <iframe
          src={element.url}
          height={element.height || DEFAULT_VALUE_FOR_IFRAME}
          className={styles.iframe}
          contentEditable={false}
        >
          {props.children}
        </iframe>
      </TopPanelButtonsWrapper>
    </div>
  )
}
